import { Body, Controller, Get, HttpCode, HttpStatus, Param, Post, Query, Req, UseGuards } from '@nestjs/common';
import { ApiKeyGuard, ApplicationScopedRequest } from '../common/guards/api-key.guard';
import { ChatService } from './chat.service';
import { CreateSessionDto } from './dto/create-session.dto';
import { SendMessageDto } from './dto/send-message.dto';
import { PresenceService } from './presence.service';

@UseGuards(ApiKeyGuard)
@Controller('chat')
export class ChatController {
  constructor(
    private readonly chatService: ChatService,
    private readonly presence: PresenceService,
  ) {}

  @Post('sessions')
  createSession(@Req() request: ApplicationScopedRequest, @Body() dto: CreateSessionDto) {
    return this.chatService.createSession(request.application.id, dto);
  }

  // Must come before the :sessionId route below — Nest/Express would
  // otherwise match "mine" as a sessionId param.
  @Get('sessions/mine')
  findMySession(@Req() request: ApplicationScopedRequest, @Query('externalUserId') externalUserId?: string) {
    if (!externalUserId) return null;
    return this.chatService.findMySession(request.application.id, externalUserId);
  }

  @Get('sessions/:sessionId')
  getSession(@Req() request: ApplicationScopedRequest, @Param('sessionId') sessionId: string) {
    return this.chatService.getSession(request.application.id, sessionId);
  }

  @Get('sessions/:sessionId/messages')
  listMessages(@Req() request: ApplicationScopedRequest, @Param('sessionId') sessionId: string) {
    return this.chatService.listMessages(request.application.id, sessionId);
  }

  @Post('sessions/:sessionId/messages')
  sendMessage(
    @Req() request: ApplicationScopedRequest,
    @Param('sessionId') sessionId: string,
    @Body() dto: SendMessageDto,
  ) {
    return this.chatService.sendMessage(request.application.id, sessionId, dto.content);
  }

  @Post('sessions/:sessionId/escalate')
  escalate(@Req() request: ApplicationScopedRequest, @Param('sessionId') sessionId: string) {
    return this.chatService.escalate(request.application.id, sessionId);
  }

  // The customer's own toggle back to bot answers — see
  // ChatService.switchToBot() for why this never touches the ticket.
  @Post('sessions/:sessionId/switch-to-bot')
  switchToBot(@Req() request: ApplicationScopedRequest, @Param('sessionId') sessionId: string) {
    return this.chatService.switchToBot(request.application.id, sessionId);
  }

  // Customer-initiated end of a pure-bot conversation — 409s if a ticket
  // already exists (that has to close through the agent workflow instead).
  @Post('sessions/:sessionId/close')
  closeSession(@Req() request: ApplicationScopedRequest, @Param('sessionId') sessionId: string) {
    return this.chatService.closeSession(request.application.id, sessionId);
  }

  // Is anyone actually there? Polled by the panel while escalated, alongside
  // messages. getSession enforces the same session-ownership 404 as the rest.
  // Also carries the ticket's own number/status — the same poll is how the
  // widget picks up an agent changing status server-side, no separate timer.
  @Get('sessions/:sessionId/presence')
  async getPresence(@Req() request: ApplicationScopedRequest, @Param('sessionId') sessionId: string) {
    await this.chatService.getSession(request.application.id, sessionId);
    const ticket = await this.chatService.findTicket(sessionId);

    // Only ever the *assigned* agent's real presence — never a store-wide
    // "someone is online" stand-in. Showing e.g. "Store Admin · online"
    // before anyone has actually picked up this ticket falsely implies that
    // specific person is watching it; null here tells the widget to render
    // the ticket's status (new/unassigned) instead.
    const agent = ticket?.assignedAgentId
      ? this.presence.agentPresenceById(ticket.assignedAgentId, ticket.assignedAgentName ?? undefined)
      : null;

    return {
      agent,
      agentTyping: this.presence.isTyping(sessionId, 'agent'),
      typingAgentName: this.presence.typingAgentName(sessionId),
      ticket: ticket ? { srNumber: ticket.srNumber, status: ticket.status } : null,
    };
  }

  @Post('sessions/:sessionId/typing')
  @HttpCode(HttpStatus.NO_CONTENT)
  async typing(@Req() request: ApplicationScopedRequest, @Param('sessionId') sessionId: string): Promise<void> {
    await this.chatService.getSession(request.application.id, sessionId);
    this.presence.markTyping(sessionId, 'shopper');
  }
}
